import { AsyncSelect, type IAsyncSelectProps } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { accountRepository } from '@webapp/platform/finance'
import messages from './messages'

export interface ExpenseAccountSelectValue {
  id: number
  accountCode: {
    id: number | undefined
    code: string
    description: string
  }
}

export const getExpenseAccountLabel = (
  value: ExpenseAccountSelectValue
): string => `${value.accountCode.code} - ${value.accountCode.description}`

export interface IExpenseAccountCodeSelectProps<
  IsMulti extends boolean = false,
> extends IAsyncSelectProps<ExpenseAccountSelectValue, IsMulti> {
  requesterId?: number
  secondaryCategoryId?: number
}

export const ExpenseAccountCodeSelect = ({
  requesterId,
  secondaryCategoryId,
  cacheOptions = true,
  clearCacheOnMenuClose = false,
  ...props
}: IExpenseAccountCodeSelectProps) => {
  const intl = useProcurifyIntl()

  const loadOptions: IAsyncSelectProps<ExpenseAccountSelectValue>['loadOptions'] =
    async (inputValue, pagination, callback) => {
      try {
        const res = await accountRepository.getRequestableAccounts(
          secondaryCategoryId,
          {
            requester: requesterId,
            search: inputValue,
            page: pagination.page,
          }
        )
        callback({
          options: res.data.map((account) => ({
            id: account.id,
            accountCode: {
              id: account.accountCode.id,
              code: account.accountCode.code,
              description: account.accountCode.description,
            },
          })),
          pagination: {
            hasMore: res.pagination
              ? res.pagination.page < res.pagination.numPages
              : false,
            page: pagination.page + 1,
          },
        })
      } catch {
        callback({
          options: [],
          pagination: { hasMore: false, page: pagination.page },
        })
      }
    }

  return (
    <AsyncSelect<ExpenseAccountSelectValue>
      cacheOptions={cacheOptions}
      clearCacheOnMenuClose={clearCacheOnMenuClose}
      loadOptions={loadOptions}
      placeholder={intl.formatMessage(messages.placeholderText)}
      getOptionLabel={getExpenseAccountLabel}
      getOptionValue={(option) => (option ? option.id.toString() : '')}
      {...props}
    />
  )
}
